// How a cookbook's recipe cards look: a picture or the recipe's name on the front, the recipe itself on the back. The
// builder's cards and the PDF (cookbook/cards.ts) are drawn by the same code from these choices. The named looks are
// starting points; changing any choice makes it the maker's own. Nothing here changes a photo: edge to edge crops only
// what's printed, and a photo of the card itself is never cut into when it's framed.

import { readDesign, RECIPE_CARD, RECIPE_CARDS, type PageEdits } from '../../../shared/printDesign'
import type { CookbookPage, Picture } from '../cookbooks'

export type CardLook = {
  // The front: the photo of the original card, a photo of the dish, or the recipe's name alone. A recipe without the
  // photo asked for shows the other one, or its name.
  front: (typeof RECIPE_CARDS.front)[number]
  // The photo runs off the edges, or sits whole in a white border with the recipe's name under it.
  edges: (typeof RECIPE_CARDS.edges)[number]
  type: (typeof RECIPE_CARDS.type)[number]
  paper: (typeof RECIPE_CARDS.paper)[number]
  // How the photos print: as they are, black and white, or sepia.
  photos: (typeof RECIPE_CARDS.photos)[number]
}

export const CARD_LOOKS = {
  original: {
    name: 'Original card',
    blurb: 'Their handwriting on the front',
    look: { front: 'card', edges: 'bleed', type: 'traditional', paper: 'natural', photos: 'color' },
  },
  dish: {
    name: 'Dish photo',
    blurb: 'The food on the front',
    look: { front: 'dish', edges: 'bleed', type: 'modern', paper: 'white', photos: 'color' },
  },
  framed: {
    name: 'Framed',
    blurb: 'Photo with a white border',
    look: { front: 'dish', edges: 'border', type: 'traditional', paper: 'white', photos: 'color' },
  },
  words: {
    name: 'Words only',
    blurb: 'Just the name, clean type',
    look: { front: 'title', edges: 'border', type: 'traditional', paper: 'natural', photos: 'color' },
  },
} as const satisfies Record<string, { name: string; blurb: string; look: CardLook }>
export type CardLookId = keyof typeof CARD_LOOKS
export const CARD_LOOK_IDS = Object.keys(CARD_LOOKS) as CardLookId[]

export const sameCardLook = (a: CardLook, b: CardLook) =>
  (Object.keys(a) as (keyof CardLook)[]).every((k) => a[k] === b[k])

// A recipe's card given choices of its own, by the recipe's id.
export type CardEdits = PageEdits<typeof RECIPE_CARD>
export type CardEdit = CardEdits[string]

export type ChosenCards = { from: CardLookId; look: CardLook; pages?: CardEdits }
export const readChosenCards = (value: unknown): ChosenCards | null => readDesign(RECIPE_CARDS, value, RECIPE_CARD)

// The sizes a card is printed at, wider than tall, as a recipe card is kept in a box.
export const CARD_SIZES = [
  { id: '4x6', label: '4 × 6 in', trim: { width: 6, height: 4 } },
  { id: '5x7', label: '5 × 7 in', trim: { width: 7, height: 5 } },
] as const
export type CardSize = (typeof CARD_SIZES)[number]

// What a card's front shows: the photo of the card itself, a photo of the dish, or the recipe's name. A recipe without
// the photo asked for shows the other one, and with neither, its name.
export type Front = { kind: 'card' | 'dish'; picture: Picture } | { kind: 'title' }
export function frontOf(page: CookbookPage, look: Pick<CardLook, 'front'>): Front {
  const photos = page.recipe.photos
  const ofCard = photos.find((x) => x.of === 'card')
  const card = ofCard ? ({ ...ofCard, kind: 'recipe' } as Picture) : null
  // The page's own picture is the dish, unless it's the photo of the card; then the recipe's first of the dish.
  const shown =
    page.picture && !(page.picture.kind === 'recipe' && page.picture.id === ofCard?.id) ? page.picture : null
  const ofDish = photos.find((x) => x.of === 'food')
  const dish = shown ?? (ofDish ? ({ ...ofDish, kind: 'recipe' } as Picture) : null)
  const asked = look.front === 'card' ? [card, dish] : look.front === 'dish' ? [dish, card] : []
  const [picture] = asked.filter((x) => x !== null)
  if (!picture) return { kind: 'title' }
  return { kind: picture === card ? 'card' : 'dish', picture }
}
